import * as React from 'react'
// libs
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { formValueSelector, reset } from 'redux-form'
// components
import { DrawerModal } from 'components/UI'
import { FloatingBottomPaper } from 'components/FormElements/FloatingActions/views'
// context
import { KPIPageContext } from 'containers/KPIs/Register/layers/API'
import { KPICreationContext } from 'context/index'
// constants
import { ENTITIES, Forms, INPUT_METHOD_VALUES, KPI_RESULT_TYPE_VALUES, Modals } from 'constants/index'
// hooks
import { useDefaultObjectSecurity, useModalData } from 'hooks/index'
// themes
import { CloseIcon } from 'components/UI/Icons'
import { Header, Wrapper as Scrollbar } from 'components/Modals/PlanItems/styles'
// view components
import DetailsForm from 'components/Modals/PlanItems/KPI/MinimizedModal/components/DetailsSection/components/DetailsForm' // utils
// utils
import {
  generateRecurrenceLabel,
  generateScaleLabel,
  getFieldValues,
  getOptionFromFields,
  getOptionsByEntity,
  getValueFromField,
  getValueFromOption,
  getValuesFromOptions,
  ifElse
} from 'utils/index'
import { handleError } from 'utils/errors/handlers'
import { get, isEmpty, size } from 'lodash'

const PAPER_PROPS = {
  style: {
    height: '100%',
    overflow: 'hidden',
    top: 0
  }
}

const MODAL_PROPS = { disableBackdropClick: true }

const Wrapper = styled('div')`
  margin: 0 16px 80px 16px;

  ${FloatingBottomPaper} {
    width: 600px;
    right: 0;
  }
`

const selector = formValueSelector(Forms.KPI_CREATE_FORM)

const KPICreateModal: React.FC = () => {
  const dispatch = useDispatch()

  const { fields, refetch, kpiCreate, planItemCreate } = React.useContext(KPIPageContext)

  const { plan, recurrence, resultTarget, resultTypeScale } = useSelector((state) => ({
    plan: selector(state, 'plan'),
    recurrence: selector(state, 'recurrence'),
    resultTarget: selector(state, 'resultTarget'),
    resultTypeScale: selector(state, 'resultTypeScale')
  }))

  const defaultObjectSecurities = useDefaultObjectSecurity({
    objectApiName: 'kpis',
    planId: get(plan, 'value')
  })

  const {
    hideModal,
    modalData: { isOpen }
  } = useModalData({ modalType: Modals.KPI_CREATE_MODAL })

  const handleFormSubmit = async (data) => {
    const parentId = getValueFromOption(data.parent)
    const planId = getValueFromOption(data.plan)

    const input = {
      description: data.description,
      fieldValues: getFieldValues(fields, data, defaultObjectSecurities),
      memberPositionIds: getValuesFromOptions(getOptionsByEntity(data.membersUserPosition, ENTITIES.POSITION)),
      memberUserIds: getValuesFromOptions(getOptionsByEntity(data.membersUserPosition, ENTITIES.USER)),
      ownedById: getValueFromOption(data.ownedByEmployeeOrPosition),
      recurrence: data.recurrence,
      resultBaseline: data.resultBaseline,
      resultBaselineScale: getValueFromOption(data.resultBaselineScale),
      resultTarget: data.resultTarget,
      resultType: getValueFromField(data.resultInputType, fields, 'resultInputType'),
      resultTypeScale: data.resultTypeScale
    }

    try {
      planId
        ? await planItemCreate({
            variables: {
              input: {
                planId: planId,
                parentId: parentId || null,
                planKPIInput: input
              }
            }
          })
        : await kpiCreate({
            variables: {
              input: input
            }
          })

      await refetch()

      dispatch(reset(Forms.KPI_CREATE_FORM))
      hideModal()
    } catch (error) {
      handleError(error, 'KPICreateModal.handleFormSubmit')
    }
  }

  const initialValues = {
    inputMethod: getOptionFromFields(fields, 'inputMethod', INPUT_METHOD_VALUES.USER_INPUT),
    recurrenceText: isEmpty(recurrence) ? 'Set Indicator Recurrence' : generateRecurrenceLabel(recurrence),
    resultInputType: getOptionFromFields(fields, 'resultInputType', KPI_RESULT_TYPE_VALUES.VALUE),
    resultTargetText: isEmpty(resultTarget) ? 'Set Result Targets' : 'Modify Result Targets',
    resultTypeScaleText: ifElse(size, () => generateScaleLabel(resultTypeScale), 'Set Indicator Scale')(resultTypeScale)
  }

  const value = { fields, isCreateForm: true }

  return (
    <DrawerModal
      width={'600px'}
      isOpen={isOpen}
      hideModal={hideModal}
      ModalProps={MODAL_PROPS}
      PaperProps={PAPER_PROPS}>
      <Header>
        <span>Create Indicator</span>
        <CloseIcon onClick={hideModal} />
      </Header>
      <Scrollbar>
        <Wrapper>
          <KPICreationContext.Provider value={value}>
            <DetailsForm
              onCancelClick={hideModal}
              onSubmit={handleFormSubmit}
              form={Forms.KPI_CREATE_FORM}
              initialValues={initialValues}
              keepDirtyOnReinitialize={true}
            />
          </KPICreationContext.Provider>
        </Wrapper>
      </Scrollbar>
    </DrawerModal>
  )
}

export default KPICreateModal
